'use client';
import React from 'react';
import {
  useKeywordSearchModule,
  AutocompleteModule,
  createKeywordAutocompleteModule,
  ActionModule,
  createKeywordTagModule,
  TagModule,
  ClipboardModule,
} from '../../Modules';
import {
  SearchItem,
  KeywordModuleId,
  createKeywordSearchItem,
  isKeywordSearchItem,
  KeywordSearchItemDataV1,
  KeywordSearchItemVersionV1,
} from '@benzinga/search-modules';
import { TokenData } from '../tokenize';
import { ExpressionModule, ExpressionSearchModule } from './ExpressionModules';
import { AutocompleteDropdownItem, SearchTag, dropdownDescriptionHelper } from '../../SearchBar';
import styled, { css } from '@benzinga/themetron';
import { TrendingTopicsManager } from '@benzinga/trending-topics-manager';
import { SessionContext } from '@benzinga/session-context';
import { SearchContext, SearchContextType } from '../../context';

export interface KeywordExpressionData {
  leftOperand: 'Title' | 'Body' | 'Fulltext';
  operator: 'phrase';
  rightOperand: string;
}

export interface KeywordExpressionModuleDataV1 extends KeywordSearchItemDataV1 {
  mode: KeywordExpressionData['leftOperand'];
}

export interface KeywordExpressionModuleItemV1 extends SearchItem<KeywordExpressionModuleDataV1> {
  version: typeof KeywordSearchItemVersionV1;
  moduleId: typeof KeywordModuleId;
}

export type KeywordExpressionModuleData = KeywordExpressionModuleDataV1;
export type KeywordExpressionModuleItem = KeywordExpressionModuleItemV1;

export const isKeywordExpressionModuleItem = (item: SearchItem): item is KeywordExpressionModuleItem =>
  isKeywordSearchItem(item) && 'mode' in item.data;

export const createKeywordExpressionItem = (
  keyword: string,
  mode: KeywordExpressionModuleDataV1['mode'],
): KeywordExpressionModuleItem => {
  const item = createKeywordSearchItem(keyword);
  return { ...item, data: { ...item.data, mode } };
};

export const createKeywordExpressionActionModule = (
  prevMode: React.MutableRefObject<KeywordExpressionData['leftOperand']>,
): ActionModule => {
  return {
    dropdown: (item: SearchItem) => {
      if (!isKeywordExpressionModuleItem(item)) return [];
      return [
        {
          action: expr => {
            if (!expr) return [expr, {}];
            const item = expr.expressions[expr.index];
            if (item.operator !== 'expression' || !isKeywordExpressionModuleItem(item)) return [expr, {}];
            const mode = item.data.mode;
            const nextMode: typeof mode = mode === 'Body' ? 'Fulltext' : mode === 'Fulltext' ? 'Title' : 'Body';
            item.data.mode = nextMode;
            prevMode.current = nextMode;
            return [expr, {}];
          },
          component: dropdownDescriptionHelper('Search Toggle', `Current search mode: ${item.data.mode}`),
          id: 'Search Toggle',
          type: 'ActionDropdown',
        },
      ];
    },
  };
};

export interface KeywordExpressionModule extends ExpressionModule<KeywordExpressionModuleData> {
  toTokenData: (item: SearchItem) => KeywordExpressionData | undefined;
  fromTokenData: (token: TokenData<unknown>) => KeywordExpressionModuleItem | undefined;
}

export const isKeywordExpressionData = (token: TokenData<unknown>): token is KeywordExpressionData => {
  return (
    (token.leftOperand === 'Title' || token.leftOperand === 'Body' || token.leftOperand === 'Fulltext') &&
    token.operator === 'phrase'
  );
};

export const createKeywordExpressionModule = (): KeywordExpressionModule => {
  return {
    fromTokenData: token => {
      if (!isKeywordExpressionData(token)) return undefined;
      return createKeywordExpressionItem(token.rightOperand, token.leftOperand);
    },
    toTokenData: item => {
      if (!isKeywordExpressionModuleItem(item)) return undefined;
      return {
        leftOperand: item.data.mode,
        operator: 'phrase',
        rightOperand: item.data.keyword,
      };
    },
  };
};

export const createKeywordExpressionAutocompleteModule = (
  prevMode: React.MutableRefObject<KeywordExpressionData['leftOperand']>,
  trendingTopicsManager: TrendingTopicsManager,
  searchContext: SearchContextType,
): AutocompleteModule<KeywordExpressionModuleData> => {
  const autocomplete = createKeywordAutocompleteModule(trendingTopicsManager, searchContext);
  return {
    ...autocomplete,
    dropdown: item => {
      if (!isKeywordExpressionModuleItem(item)) return undefined;
      const d = autocomplete.dropdown(item);
      return (d
        ? ({ ...d, searchItem: item } as AutocompleteDropdownItem<KeywordExpressionModuleData>)
        : d) as unknown as any;
    },
    query: (text, limit, selectedItems) => {
      const promise = autocomplete.query(text, limit, selectedItems);
      return {
        ...promise,
        promise: new Promise(resolve => {
          promise.promise.then(res => {
            if (res.ok) {
              return resolve({
                canceled: false,
                ok: res.ok.map(item => [{ ...item[0], data: { ...item[0].data, mode: prevMode.current } }, item[1]]),
              });
            }
            return resolve(res as unknown as any);
          });
        }),
      };
    },
  };
};

export const createKeywordExpressionTagModule = (): TagModule => {
  const tag = createKeywordTagModule();
  return {
    ...tag,
    render: item => {
      if (isKeywordExpressionModuleItem(item)) {
        return {
          jsx: <KeywordTag mode={item.data.mode}>{item.data.keyword}</KeywordTag>,
          type: 'custom',
        };
      }
      return undefined;
    },
  };
};

export const createKeywordExpressionClipboardModule = (): ClipboardModule => {
  return {
    onCopyJSON: token => {
      if (!isKeywordExpressionModuleItem(token)) return undefined;
      return [token];
    },
    onPasteJSON: (token): KeywordExpressionModuleItem | undefined => {
      if (isKeywordSearchItem(token)) return { ...token, data: { ...token.data, mode: 'Fulltext' } };
      if (isKeywordExpressionModuleItem(token)) return token;
      return undefined;
    },
  };
};

export const useKeywordExpressionSearchModule = (): ExpressionSearchModule => {
  const prevMode = React.useRef<KeywordExpressionData['leftOperand']>('Fulltext');
  const keywordModule = useKeywordSearchModule();
  const session = React.useContext(SessionContext);
  const search = React.useContext(SearchContext);
  const trendingTopicsManager = session.getManager(TrendingTopicsManager);

  return React.useMemo(
    () => ({
      ...keywordModule,
      action: createKeywordExpressionActionModule(prevMode),
      autocomplete: createKeywordExpressionAutocompleteModule(prevMode, trendingTopicsManager, search),
      clipboard: createKeywordExpressionClipboardModule(),
      expression: createKeywordExpressionModule(),
      tag: createKeywordExpressionTagModule(),
    }),
    [keywordModule, search, trendingTopicsManager],
  );
};

export const KeywordTag = styled(SearchTag)<{ selected?: boolean; mode: 'Fulltext' | 'Body' | 'Title' }>`
  ${props =>
    props.mode === 'Body'
      ? css`
          border-left: 4px solid ${props => props.theme.colors.backgroundActive};
          background: ${props => props.theme.colors.brand};
        `
      : ``}
  ${props =>
    props.mode === 'Title'
      ? css`
          border-left: 4px ridge ${props => props.theme.colors.brand};
          background: ${props => props.theme.colors.brand};
        `
      : ``}
${props =>
    props.mode === 'Fulltext'
      ? css`
          border-left: 4px ridge ${props => props.theme.colors.brand};
          background: ${props => props.theme.colors.brand};
        `
      : ``}
`;
